/* Insights pages: shared layout, KPI tiles, controls, the library and the small parts. */

.stack {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
  gap: 16px;
}

.split {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: 28px;
  align-items: start;
}

.halves {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  align-items: start;
}

/* Three cards in a row end at the same height, as on the boards. */
.thirds {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
  align-items: stretch;
}

@media (max-width: 1100px) {
  .split,
  .halves,
  .thirds {
    grid-template-columns: minmax(0, 1fr);
  }
}

.muted {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.note {
  margin: 0;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.5;
}

.cardMeta {
  color: var(--text-secondary);
  font-size: 13px;
}

.strong {
  color: var(--brand-navy);
  font-weight: var(--fw-bold);
}

.link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  color: var(--text-link);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  text-decoration: underline;
}

.link[data-hovered],
.link:hover {
  color: var(--text-link-hover-on-light);
}

.link[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.footRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  padding-top: 12px;
  border-top: 1px solid var(--border-hairline);
}

/* --- KPI tiles --- */

.kpi {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 20px 22px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
}

.kpiMuted {
  border-style: dashed;
  border-color: var(--border-dashed);
  background: var(--surface-ground);
}

.kpiHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.kpiLabel {
  margin: 0;
  color: var(--text-secondary);
  font-size: 11px;
  line-height: 1.5;
}

.kpiValueRow {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
  margin: 0;
}

.kpiValue {
  color: var(--brand-navy);
  font-size: 34px;
  line-height: 1.1;
}

.kpiUnit {
  color: var(--text-secondary);
  font-size: 14px;
}

.kpiNote {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.kpiLink {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  min-height: 44px;
  margin-top: auto;
  color: var(--text-link);
  font-size: 13px;
  font-weight: var(--fw-semibold);
  text-decoration: underline;
}

.kpiLink[data-hovered] {
  color: var(--text-link-hover-on-light);
}

.kpiLink[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.meter {
  display: block;
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--chart-track);
  overflow: hidden;
}

.meterFill {
  display: block;
  height: 100%;
  border-radius: var(--radius-pill);
  background: var(--brand-navy);
}

/* --- Controls --- */

.filterBar {
  position: relative; /* for Help's "Shown from Help" tag on its edge */
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
}

.filterHead {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filterTitle {
  margin: 0;
  color: var(--brand-navy);
  font-weight: var(--fw-semibold);
}

/* A report's filters inside a card or a bar, for Help's "Shown from Help" tag on its edge. */
.filterMark {
  position: relative;
}

.filterRow {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px 20px;
}

.filterRow > * {
  min-width: min(200px, 100%);
}

.filterNote {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.controlLabel {
  display: block;
  margin-bottom: 6px;
  color: var(--text-secondary);
  font-size: 11px;
}

.segmented {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.segments {
  display: inline-flex;
  flex-wrap: wrap;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  overflow: hidden;
  align-self: flex-start;
}

.segment {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 14px;
  border-right: 1px solid var(--border-control);
  background: var(--white);
  color: var(--brand-navy);
  font-family: var(--font-ui);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
}

.segments > :last-child .segment {
  border-right: 0;
}

.segment[data-hovered] {
  background: var(--brand-cyan-50);
}

.segment[data-selected] {
  background: var(--brand-navy);
  color: var(--white);
}

.segment[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -3px;
}

.exportWrap {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.exportRows {
  color: var(--text-secondary);
  font-size: 13px;
}

.exportOff {
  max-width: 420px;
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.callout {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 16px 20px;
  border: 1px solid var(--border-card);
  border-left: 4px solid var(--brand-cyan);
  border-radius: var(--radius-md);
  background: var(--white);
  color: var(--text-body);
  font-size: 15px;
  line-height: 1.55;
}

.callout svg {
  flex: 0 0 auto;
  margin-top: 3px;
  color: var(--brand-petrol);
}

.banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 24px;
  padding: 18px 22px;
  border: 1px dashed var(--border-dashed);
  border-radius: var(--radius-md);
  background: var(--white);
}

.bannerText {
  flex: 1 1 320px;
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.55;
}

.bannerText strong {
  display: block;
  margin-bottom: 2px;
  color: var(--brand-navy);
  font-size: 16px;
}

/* --- Report library --- */

/* Three across (nine reports close the grid), two on a tablet, one on a phone. */
.library {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.libraryCard {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  padding: 16px 18px 12px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
}

.libraryOff {
  border-style: dashed;
  border-color: var(--border-dashed);
  background: var(--surface-ground);
}

/* The frame for each report's own sketch (LibraryArt, Library.module.css). */
.libraryArt {
  height: 64px;
  padding: 10px 14px;
  border-radius: var(--radius-sm);
  background: var(--surface-ground);
  overflow: hidden;
}

.libraryTitle {
  margin: 0;
  color: var(--brand-navy);
  font-size: 16px;
  font-weight: var(--fw-bold);
}

.libraryQuestion {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
}

.libraryStat {
  margin: 0;
  color: var(--text-body);
  font-size: 13px;
  font-weight: var(--fw-semibold);
}

.libraryFoot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: auto;
}

/* --- Recognition tiles (RPT1) --- */

.tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tile {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 72px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  background: var(--heat-1);
  color: var(--brand-navy);
  text-decoration: none;
}

.tile[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.tileBin2 {
  background: var(--heat-2);
}

/* White on the third step is 3.7:1; the darkest navy is 5:1. */
.tileBin3 {
  background: var(--heat-3);
  color: var(--brand-navy-950);
}

.tileBin4 {
  background: var(--heat-4);
  color: var(--white);
}

.tileEmpty {
  background: var(--surface-ground);
  color: var(--text-secondary);
}

/* APP-197: a sector of fewer than 5 people: no share band, a dashed edge, its counts written. */
.tileSmall {
  border: 1px dashed var(--border-dashed);
  background: var(--surface-card);
  color: var(--brand-navy);
}

.tileCompany {
  border: 1px solid var(--brand-navy);
  background: var(--white);
  color: var(--brand-navy);
}

.tileName {
  font-size: 12px;
  font-weight: var(--fw-semibold);
}

.tileValue {
  font-family: var(--font-display);
  font-size: 24px;
  line-height: 1.1;
}

.tileSub {
  font-size: 11px;
}

.binLegend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 0;
  padding: 0;
  color: var(--text-secondary);
  font-size: 11px;
  list-style: none;
}

.binLegend li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.bin {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: var(--heat-1);
}

.binSmall {
  border: 1px dashed var(--border-dashed);
  background: var(--surface-card);
}

.bin2 {
  background: var(--heat-2);
}

.bin3 {
  background: var(--heat-3);
}

.bin4 {
  background: var(--heat-4);
}

.bin0 {
  border: 1px solid var(--border-card);
  background: var(--heat-0);
}

/* --- Lists (repeat recipients, givers, recipients) --- */

.list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.listItem {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-hairline);
}

.listItem:last-child {
  border-bottom: 0;
}

.avatar {
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-circle);
  background: var(--gray-100);
  color: var(--brand-navy);
  font-size: 12px;
  font-weight: var(--fw-bold);
}

.rank {
  min-width: 18px;
  color: var(--text-muted);
  font-size: 12px;
  text-align: right;
}

.who {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.whoName {
  color: var(--brand-navy);
  font-size: 14px;
  font-weight: var(--fw-semibold);
}

.whoMeta {
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.amount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.amountValue {
  color: var(--brand-navy);
  font-size: 14px;
  font-weight: var(--fw-bold);
}

.amountMeta {
  color: var(--text-secondary);
  font-size: 12px;
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.badge {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 8px;
  border-radius: var(--radius-pill);
  background: var(--status-waiting-bg);
  color: var(--status-waiting-ink);
  font-size: 11px;
  font-weight: var(--fw-semibold);
}

.countPill {
  display: inline-grid;
  place-items: center;
  min-width: 24px;
  height: 22px;
  padding: 0 7px;
  border-radius: var(--radius-pill);
  background: var(--status-waiting-bg);
  color: var(--status-waiting-ink);
  font-size: 12px;
  font-weight: var(--fw-bold);
}

/* --- Tables Insights draws itself (the register, the heat map, the ledger) --- */

.scroll {
  /* Positioned, so words kept only for screen readers (absolutely placed) inside a wide table
     stay inside this scroller instead of widening the page at phone width. */
  position: relative;
  max-width: 100%;
  overflow-x: auto;
}

.scroll:focus-visible {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.table caption {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.table th {
  padding: 10px 10px;
  border-bottom: 1px solid var(--border-card);
  color: var(--text-secondary);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: var(--fw-medium);
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.table td {
  padding: 12px 10px;
  border-bottom: 1px solid var(--border-hairline);
  vertical-align: top;
}

.table tbody th {
  font-family: var(--font-body);
  font-size: 13px;
  letter-spacing: 0;
  text-transform: none;
  color: var(--text-body);
  font-weight: var(--fw-regular);
  vertical-align: top;
  white-space: normal;
}

.num {
  text-align: right !important;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.nowrap {
  white-space: nowrap !important;
}

.table tfoot th {
  font-family: var(--font-body);
  font-size: 13px;
  letter-spacing: 0;
  text-transform: none;
}

.totalRow td,
.totalRow th {
  border-top: 2px solid var(--brand-navy);
  border-bottom: 0;
  background: var(--surface-soft);
  color: var(--brand-navy);
  font-weight: var(--fw-bold) !important;
}

.groupRow th {
  padding-top: 16px;
  background: var(--surface-soft);
  color: var(--brand-navy) !important;
  font-weight: var(--fw-bold) !important;
}

.emptyCell {
  padding: 24px 16px !important;
  background: var(--surface-soft);
  color: var(--text-secondary);
  text-align: center;
  line-height: 1.55;
}

.emptyCell strong {
  color: var(--brand-navy);
}

.cellSub {
  display: block;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.paidFromHead {
  display: block;
  color: var(--text-secondary);
  font-family: var(--font-ui);
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
}

.pagerButtons {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.reconcile {
  margin: 0;
  padding: 14px 16px;
  border-radius: var(--radius-sm);
  background: var(--brand-cyan-50);
  color: var(--text-body);
  font-size: 13px;
  line-height: 1.55;
}

/* A card's own controls on the right of its heading: a view switch and an export. */
.cardActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px 12px;
}

/* Saved views (X6): the person's own views, a save form and a manage list. */
.savedViews {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.saveButtons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

/* The name field and its buttons line up through FieldWithAction (APP-128). */
.saveForm {
  max-width: 720px;
}

.manageList {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid var(--border-card);
}

.manageItem {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-card);
}

.manageName {
  color: var(--brand-navy);
  font-weight: var(--fw-semibold);
}

.formError {
  margin: 0;
  color: var(--status-problem-ink);
  font-size: 14px;
}

/* The register's "Where it is": the plain stage as a pill, its detail under it in grey. */
.stageCell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 170px;
}

/* The overview's period and its exports, side by side; stacked on a phone. */
.periodBar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px 24px;
}

@media print {
  .periodBar .cardActions {
    display: none;
  }
}

/* Over budget, in words and colour: the word carries it, the colour only repeats it. */
.badgeProblem {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 8px;
  border-radius: var(--radius-pill);
  background: var(--status-problem-bg);
  color: var(--status-problem-ink);
  font-size: 11px;
  font-weight: var(--fw-semibold);
}

@media (max-width: 1100px) {
  .library {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .library {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* A link that leads a table cell keeps its 44 px target but lines its words up with the
   row's first line (the date beside it), as on the boards. */
.table td > .link:first-child,
.table tbody th > .link:first-child {
  margin-top: -12px;
  margin-bottom: -12px;
}
